import { useEffect } from 'react';
import { errorHandler } from '@/lib/errorHandling';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

export function GlobalErrorHandler() {
  const networkStatus = useNetworkStatus();

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      errorHandler.handleError(event.reason, {
        context: 'Unhandled Promise Rejection',
        showToast: true,
        logError: true,
      });
      
      // Prevent the default browser behavior
      event.preventDefault();
    };

    // Handle global JavaScript errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      
      errorHandler.handleError(event.error || event.message, {
        context: 'Global Error',
        showToast: true,
        logError: true,
      });
    };

    // Handle resource loading errors
    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target && target.tagName) {
        console.error(`Resource loading error: ${target.tagName}`, target);
        
        errorHandler.handleError(`Failed to load ${target.tagName.toLowerCase()}`, {
          context: 'Resource Loading',
          showToast: false, // Don't show toast for resource errors
          logError: true,
        });
      }
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('error', handleResourceError, true); // Capture phase for resource errors

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('error', handleResourceError, true);
    };
  }, []);

  // Monitor network status changes and show appropriate messages
  useEffect(() => {
    if (!networkStatus.isOnline) {
      errorHandler.showWarning(
        'You are currently offline. Some features may not work properly.',
        'Connection Lost'
      );
    }
  }, [networkStatus.isOnline]);

  // This component doesn't render anything
  return null;
}